import { useEffect, useRef, useState } from "react";
import { Sigma } from "lucide-react";
import { MathText, loadKatex } from "./MathText";
import { guidePoints, guideProblem, hasMath, insertAt, wrapEquation } from "../lib/markingGuide";
import type { TexEngine } from "../lib/markingGuide";

/** The one part of MathLive's `<math-field>` this screen uses. */
interface MathField extends HTMLElement {
  value: string;
}
type MathFieldClass = new () => MathField;

let fieldClass: Promise<MathFieldClass> | null = null;

/**
 * MathLive is big, so it is fetched the first time a worker opens the equation
 * panel — never by screens that don't need it. A failed fetch is forgotten, so
 * the next click tries again.
 */
function loadMathField(): Promise<MathFieldClass> {
  fieldClass ??= Promise.all([import("mathlive"), import("mathlive/fonts.css")])
    .then(([module]) => {
      const element = module.MathfieldElement;
      // The fonts come from the stylesheet above; the sounds aren't wanted.
      element.fontsDirectory = null;
      element.soundsDirectory = null;
      return element as unknown as MathFieldClass;
    })
    .catch((e: unknown) => {
      fieldClass = null;
      throw e;
    });
  return fieldClass;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
}

/**
 * The marking guide for a theory question. It stays a plain text box — what is
 * typed is what is stored — with an equation editor that writes `\(…\)` at the
 * cursor, and a preview of the numbered points the student will see.
 */
export function MarkingGuideInput({ value, onChange }: Props) {
  const box = useRef<HTMLTextAreaElement>(null);
  const holder = useRef<HTMLDivElement>(null);
  const field = useRef<MathField | null>(null);
  // Where the equation goes: the selection when the panel was opened.
  const selection = useRef<[number, number]>([0, 0]);
  const cursor = useRef<number | null>(null);
  const [panel, setPanel] = useState<"closed" | "loading" | "open">("closed");
  const [unavailable, setUnavailable] = useState(false);
  const [engine, setEngine] = useState<TexEngine | null>(null);

  // KaTeX checks the equations; only fetched once there are some.
  useEffect(() => {
    if (!engine && hasMath(value)) void loadKatex().then(setEngine);
  }, [engine, value]);

  // Put the cursor back after an insert, just past the new equation.
  useEffect(() => {
    if (cursor.current === null || !box.current) return;
    box.current.focus();
    box.current.setSelectionRange(cursor.current, cursor.current);
    cursor.current = null;
  }, [value]);

  const openPanel = async () => {
    const el = box.current;
    selection.current = el ? [el.selectionStart, el.selectionEnd] : [value.length, value.length];
    setPanel("loading");
    try {
      const MathFieldElement = await loadMathField();
      setUnavailable(false);
      setPanel("open");
      const mf = new MathFieldElement();
      mf.setAttribute("aria-label", "Equation");
      mf.className = "block w-full rounded-lg border border-border bg-bg px-3 py-2 text-[16px]";
      holder.current?.replaceChildren(mf);
      field.current = mf;
      mf.focus();
    } catch {
      setUnavailable(true);
      setPanel("closed");
    }
  };

  const closePanel = () => {
    holder.current?.replaceChildren();
    field.current = null;
    setPanel("closed");
  };

  const insert = () => {
    const latex = field.current?.value.trim() ?? "";
    if (latex) {
      const [start, end] = selection.current;
      const next = insertAt(value, start, end, wrapEquation(latex));
      cursor.current = next.cursor;
      onChange(next.text);
    }
    closePanel();
  };

  const points = guidePoints(value);
  const problem = guideProblem(value, engine);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label
          htmlFor="marking-guide"
          className="block text-[11px] font-semibold uppercase tracking-wider text-text-muted"
        >
          Marking Guide
        </label>
        <button
          type="button"
          onClick={() => void openPanel()}
          disabled={panel !== "closed"}
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-accent hover:underline disabled:opacity-50"
        >
          <Sigma size={13} aria-hidden="true" />
          {panel === "loading" ? "Loading…" : "Insert equation"}
        </button>
      </div>
      <textarea
        id="marking-guide"
        ref={box}
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="One point per line, or separate points with ;"
        className="w-full resize-y rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none placeholder:text-text-muted"
      />
      <p className="mt-1 text-[12px] text-text-muted">
        Students see each line, or each part between ;, as a numbered point.
      </p>

      {unavailable ? (
        <p role="status" className="mt-2 text-[12px] text-text-muted">
          The equation editor didn't load. You can still type an equation between \( and \).
        </p>
      ) : null}

      {/* Kept mounted while open, so MathLive's field has somewhere to live. */}
      <div
        hidden={panel !== "open"}
        className="mt-2 rounded-lg border border-border bg-surface p-3"
        aria-label="Equation editor"
        role="group"
      >
        <div ref={holder} />
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={insert}
            className="rounded-lg bg-accent px-4 py-1.5 text-[13px] font-semibold text-bg hover:bg-accent-light"
          >
            Insert
          </button>
          <button
            type="button"
            onClick={closePanel}
            className="rounded-lg border border-border bg-surface px-4 py-1.5 text-[13px] text-text-muted hover:text-text"
          >
            Cancel
          </button>
        </div>
      </div>

      {points.length ? (
        <div className="mt-3 rounded-lg border border-border bg-surface p-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            What students see
          </p>
          <ol aria-label="Marking guide preview" className="flex flex-col gap-1.5">
            {points.map((point, index) => (
              <li key={index} className="flex gap-2 text-[13.5px] leading-[1.5] text-text">
                <span className="shrink-0 font-semibold text-text-muted">{index + 1}.</span>
                <MathText text={point} />
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {problem ? <p className="mt-2 text-[12px] text-negative">{problem}</p> : null}
    </div>
  );
}
